import type { QuizQuestion } from "@/components/TypeformQuiz";

// Independent style-exploration heuristic, not an official Kibbe test. Every option maps to a letter:
// a = extreme yang, b = soft/blunt yang, c = balanced, d = soft yin, e = extreme yin.

export const KIBBE_QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: "height",
    title: "Height",
    description: "How tall are you?",
    helpText: "Kibbe 'vertical line' starts with literal height.",
    options: [
      { label: "5'8\" (173 cm) or taller", value: "a" },
      { label: "5'6\" – 5'7\" (168–172 cm)", value: "b" },
      { label: "5'4\" – 5'5\" (163–167 cm)", value: "c" },
      { label: "5'2\" – 5'3\" (157–162 cm)", value: "d" },
      { label: "5'1\" (155 cm) or shorter", value: "e" },
    ],
  },
  {
    id: "shoulders",
    title: "Shoulders",
    description: "Which best describes your shoulder line?",
    options: [
      { label: "Sharp and angular", value: "a" },
      { label: "Broad or blunt", value: "b" },
      { label: "Even and symmetrical", value: "c" },
      { label: "Sloped with a gentle taper", value: "d" },
      { label: "Rounded and soft", value: "e" },
    ],
  },
  {
    id: "limbs",
    title: "Arms & legs",
    description: "How do your arms and legs feel in proportion to your body?",
    options: [
      { label: "Long and narrow — sleeves and pants often run short", value: "a" },
      { label: "Long-ish, broad, and athletic", value: "b" },
      { label: "Perfectly average — most things fit", value: "c" },
      { label: "Slightly short with soft shaping", value: "d" },
      { label: "Small and delicate for my frame", value: "e" },
    ],
  },
  {
    id: "hands",
    title: "Hands & feet",
    description: "What about your hands and feet?",
    options: [
      { label: "Long, narrow, and angular", value: "a" },
      { label: "Large or broad", value: "b" },
      { label: "Moderate, in proportion", value: "c" },
      { label: "Small and slightly wide or soft", value: "d" },
      { label: "Small, narrow, and delicate", value: "e" },
    ],
  },
  {
    id: "flesh",
    title: "Body flesh",
    description: "How would you describe the flesh on your body?",
    helpText: "Think muscle/softness distribution, not weight.",
    options: [
      { label: "Lean, taut, sinewy — muscle shows as lines", value: "a" },
      { label: "Firm and muscular — I bulk easily", value: "b" },
      { label: "Evenly distributed, moderate", value: "c" },
      { label: "Soft, with gentle curve even at my leanest", value: "d" },
      { label: "Very soft and rounded — curve is my default", value: "e" },
    ],
  },
  {
    id: "waist",
    title: "Waist & hips",
    description: "How defined is your waist relative to your bust and hips?",
    options: [
      { label: "Straight up and down — barely defined", value: "a" },
      { label: "Slightly defined but broad overall", value: "b" },
      { label: "Moderately defined, balanced", value: "c" },
      { label: "Clearly defined — noticeably smaller than hips", value: "d" },
      { label: "Dramatically defined — full hourglass", value: "e" },
    ],
  },
  {
    id: "jawline",
    title: "Facial bones",
    description: "Which best describes your jawline and cheekbones?",
    options: [
      { label: "Sharp, prominent, chiseled", value: "a" },
      { label: "Broad or blunt — strong but not sharp", value: "b" },
      { label: "Even, symmetrical, softly squared", value: "c" },
      { label: "Gently tapered with soft edges", value: "d" },
      { label: "Rounded and delicate", value: "e" },
    ],
  },
  {
    id: "features",
    title: "Facial features",
    description: "And your eyes and lips?",
    options: [
      { label: "Narrow or almond eyes, straighter lips", value: "a" },
      { label: "Moderate eyes, slightly full lips on a strong face", value: "b" },
      { label: "Evenly spaced eyes, moderate lips", value: "c" },
      { label: "Large eyes or full lips on a soft face", value: "d" },
      { label: "Very large round eyes, very full lips", value: "e" },
    ],
  },
];

export type KibbeCandidate = { id: string; name: string };

// Narrow to 2-3 likely types from the letter tallies. The photo analysis
// adds a second, still uncertain estimate; this is a heuristic shortlist.
export function scoreKibbeAnswers(answers: Record<string, string>): {
  candidates: KibbeCandidate[];
  summary: string;
} {
  const tally: Record<string, number> = { a: 0, b: 0, c: 0, d: 0, e: 0 };
  for (const v of Object.values(answers)) {
    if (tally[v] !== undefined) tally[v]++;
  }

  const yang = tally.a * 2 + tally.b;
  const yin = tally.e * 2 + tally.d;
  const total = Object.values(answers).length || 1;

  const named = (ids: string[]): KibbeCandidate[] =>
    ids.map((id) => ({
      id,
      name: id
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
    }));

  // Balanced answers dominate -> Classic family
  if (tally.c >= total / 2) {
    if (yang > yin + 1) return { candidates: named(["dramatic-classic", "soft-classic"]), summary: "balanced with a sharp edge" };
    if (yin > yang + 1) return { candidates: named(["soft-classic", "dramatic-classic"]), summary: "balanced with a soft edge" };
    return { candidates: named(["soft-classic", "dramatic-classic"]), summary: "evenly balanced" };
  }

  // Strong contrast between extremes -> Gamine family
  if (tally.a >= 2 && tally.e >= 2) {
    if (yang > yin) return { candidates: named(["flamboyant-gamine", "soft-gamine"]), summary: "a high-contrast mix leaning sharp" };
    return { candidates: named(["soft-gamine", "flamboyant-gamine"]), summary: "a high-contrast mix leaning soft" };
  }

  if (yang > yin + 2) {
    // Yang-dominant: sharp -> Dramatics, blunt -> Naturals
    if (tally.a > tally.b) {
      return tally.d + tally.e >= 2
        ? { candidates: named(["soft-dramatic", "dramatic"]), summary: "long and sharp with yin undercurrent" }
        : { candidates: named(["dramatic", "soft-dramatic"]), summary: "long, sharp, and angular" };
    }
    return tally.d + tally.e >= 2
      ? { candidates: named(["soft-natural", "flamboyant-natural"]), summary: "relaxed strength with softness" }
      : { candidates: named(["flamboyant-natural", "soft-natural"]), summary: "long, blunt, and relaxed" };
  }

  if (yin > yang + 2) {
    // Yin-dominant: small + soft
    return tally.d > tally.e
      ? { candidates: named(["theatrical-romantic", "romantic"]), summary: "soft and rounded with delicate sharpness" }
      : { candidates: named(["romantic", "theatrical-romantic"]), summary: "fully soft and rounded" };
  }

  // Mild lean either way
  if (yang >= yin) return { candidates: named(["soft-natural", "soft-classic", "flamboyant-natural"]), summary: "moderately soft yang" };
  return { candidates: named(["soft-gamine", "soft-classic", "theatrical-romantic"]), summary: "moderately yin" };
}
